import { SvgIcon } from '@progress/kendo-react-common';
import { useMemo } from 'react';
import { Menu } from '../../../components/common/menu/DropdownMenu';
import { ChevronRightOutline } from '../../../icons/icons';
import { useAppSelector } from '../../hooks/store-hooks';
import { useFlightThresholds } from '../../hooks/use-flight-thresholds';
import { aircraftThresholdTypeToAirportIds, AirportThresholdsNested, isThresholdAirportAvailable } from './AirportThresholdsNested';
import { BreachedThresholdValueCell } from './BreachedThresholdValueCell';
import { MonitorStatusBadge } from './MonitorStatusBadge';

export function ContextMenuFlightThresholdsList(props: { flightId: number }) {
    const thresholds = useFlightThresholds(props.flightId);
    const showOnlyBreachedThresholds = useAppSelector((x) => x.settings.hideNonBreachedThresholdsFlightContextMenu);
    const displayOrder = useAppSelector((x) => x.global.monitorSettings.thresholds.aircraft.displayOrder);
    const definitions = useAppSelector((x) => x.global.monitorSettings.thresholds.aircraft.definitions);
    const toDisplay = useMemo(() => {
        return displayOrder
            .filter((x) => !definitions[x].disabled && !definitions[x].alertsOnly)
            .map((x) => ({ type: x, info: definitions[x] }));
    }, [displayOrder, definitions]);

    const displayTypes = useMemo(
        () =>
            showOnlyBreachedThresholds
                ? toDisplay.filter((d) => thresholds?.thresholds?.find((x) => x.type === d.type)?.breachedLevel != null)
                : toDisplay,
        [toDisplay, showOnlyBreachedThresholds, thresholds]
    );

    // Wait for the threshold to load before we display anything to avoid a quick flicker with empty table
    if (!thresholds) {
        return <></>;
    }

    return (
        <div
            className="pt-1 w-100"
            style={{
                minWidth: 300,
                display: 'grid',
                gridTemplateColumns: 'auto repeat(3, max-content)',
                columnGap: '.5rem',
                alignItems: 'center'
            }}>
            <div className="ps-2">Name</div>
            <div>Actual</div>
            <div>Threshold</div>
            <div></div>

            <div style={{ height: '.25rem', gridColumn: '1/4' }}></div>

            {displayTypes.map((threshold, i) => {
                const item = thresholds.thresholds.find((x) => x.type === threshold.type);
                return isThresholdAirportAvailable(threshold.type, thresholds) ? (
                    <Menu
                        customContent={
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'subgrid',
                                    gridTemplateRows: '24px',
                                    gridColumn: '1/5',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    backgroundColor: i % 2 ? 'var(--alternate-color)' : undefined
                                }}>
                                <div className="ps-2">{threshold.info.name}</div>
                                <div>{item?.actualValue || '-'}</div>
                                <div>
                                    <MonitorStatusBadge monitorStatus={item?.monitorStatus}>
                                        {item ? <BreachedThresholdValueCell info={threshold.info} level={item.breachedLevel} /> : '-'}
                                    </MonitorStatusBadge>
                                </div>
                                <div>
                                    <SvgIcon icon={ChevronRightOutline} size="small" />
                                </div>
                            </div>
                        }>
                        <div>
                            <div></div>
                            <AirportThresholdsNested
                                airports={aircraftThresholdTypeToAirportIds(threshold.type, thresholds)}
                                thresholdType={threshold.type}
                            />
                        </div>
                    </Menu>
                ) : (
                    <div
                        className={i % 2 ? 'alternate-bg-color' : undefined}
                        style={{ display: 'grid', gridTemplateColumns: 'subgrid', gridColumn: '1/5', height: 24, alignItems: 'center' }}>
                        <div className="ps-2">{threshold.info.name} </div>
                        <div>{item?.actualValue || '-'}</div>
                        <div>
                            <MonitorStatusBadge monitorStatus={item?.monitorStatus}>
                                {item ? <BreachedThresholdValueCell info={threshold.info} level={item.breachedLevel} /> : '-'}
                            </MonitorStatusBadge>
                        </div>
                        <div></div>
                    </div>
                );
            })}

            {displayTypes.length === 0 && (
                <div className="px-2 alternate-bg-color d-flex align-items-center" style={{ gridColumn: '1/5', height: 22 }}>
                    No breached thresholds.
                </div>
            )}
        </div>
    );
}
